/**
 * Shared types + display helpers for the Action Items surface (ADR-0053).
 * Spec: governance-foundation §15 / §19 / §21.1.
 *
 * Types mirror the hub shapes in
 *   atlas-hub/services/hub/src/actionItems/repos/actionItems.ts
 *   atlas-hub/services/hub/src/actionItems/repos/actionItemCodes.ts
 *   atlas-hub/services/hub/src/actionItems/repos/myActionItems.ts
 */

export type ActionItemStatus =
  | "open"
  | "acknowledged"
  | "in_progress"
  | "deferred"
  | "resolved"
  | "dismissed";

export type ActionItemSeverity = "critical" | "high" | "medium" | "low";
export type ActionItemPriority = "critical" | "high" | "medium" | "low";
export type ActionItemSourceKind = "manual" | "watch" | "pattern_review" | "user_provision" | "system" | "collab_session";
export type RASCIRole = "responsible" | "accountable" | "support" | "consulted" | "informed";

export interface RASCISnapshot {
  responsible_user_ids: string[];
  responsible_group_ids: string[];
  accountable_user_id: string;
  support_user_ids: string[];
  support_group_ids: string[];
  consulted_user_ids: string[];
  consulted_group_ids: string[];
  informed_user_ids: string[];
  informed_group_ids: string[];
}

export interface ActionItemRow {
  id: string;
  tenant_id: string;
  source_kind: ActionItemSourceKind;
  source_ref: Record<string, unknown>;
  title: string;
  description: string | null;
  severity: ActionItemSeverity;
  priority: ActionItemPriority;
  item_type_code_id: string;
  status: ActionItemStatus;
  assignee_user_id: string | null;
  due_at: string | null;
  rasci_snapshot: RASCISnapshot;
  deferral_reason_code_id: string | null;
  deferred_until: string | null;
  resolution_code_id: string | null;
  root_cause_code_id: string | null;
  dismissal_code_id: string | null;
  /**
   * Free-text closure explanation (migration 119). Required (non-empty
   * after trim) when transitioning to deferred/resolved/dismissed. Persists
   * across re-open — last-write-wins; never auto-cleared. Pre-119 rows are
   * NULL.
   */
  closure_notes: string | null;
  closed_by_user_id: string | null;
  closed_at: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  /** RASCI roles the caller holds on this item (computed server-side). */
  my_rasci_roles: RASCIRole[];
}

export interface RASCIMember {
  account_id: string;
  role: RASCIRole;
}

export type ActionItemCodeKind = "item_type" | "deferral_reason" | "resolution" | "root_cause" | "dismissal";

export interface ActionItemCode {
  id: string;
  tenant_id: string;
  kind: ActionItemCodeKind;
  code: string;
  label: string;
  description: string | null;
  active: boolean;
  sort_order: number;
  created_at: string;
}

export type AssignmentType = "direct" | "proxy";

export interface MyActionItemMembership {
  role: RASCIRole;
  assignment_type: AssignmentType;
  on_behalf_of_account_id: string | null;
  on_behalf_of_display_name: string | null;
}

export interface MyActionItemRow {
  id: string;
  title: string;
  source_kind: ActionItemSourceKind;
  severity: ActionItemSeverity;
  priority: string;
  status: ActionItemStatus;
  memberships: MyActionItemMembership[];
  due_at: string | null;
  created_at: string;
}

export interface ActivityRow {
  id: string;
  actor_user_id: string | null;
  actor_display_name: string | null;
  actor_email: string | null;
  event_type: string;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface Comment {
  id: string;
  author_user_id: string;
  body: string;
  created_at: string;
  edited_at: string | null;
}

// -----------------------------------------------------------------------
// URL kind mapping for code CRUD routes (spec §18.3).
// URL: hyphenated-plural; DB: singular.
// -----------------------------------------------------------------------

export type ActionItemCodeUrlKind =
  | "item-types"
  | "deferral-reasons"
  | "resolution-codes"
  | "root-cause-codes"
  | "dismissal-codes";

export const CODE_URL_KINDS: { urlKind: ActionItemCodeUrlKind; dbKind: ActionItemCodeKind; label: string }[] = [
  { urlKind: "item-types", dbKind: "item_type", label: "Item Types" },
  { urlKind: "deferral-reasons", dbKind: "deferral_reason", label: "Deferral Reasons" },
  { urlKind: "resolution-codes", dbKind: "resolution", label: "Resolution Codes" },
  { urlKind: "root-cause-codes", dbKind: "root_cause", label: "Root Causes" },
  { urlKind: "dismissal-codes", dbKind: "dismissal", label: "Dismissal Codes" }
];

// -----------------------------------------------------------------------
// Display helpers (spec §21.1 chip mappings).
// -----------------------------------------------------------------------

export function severityPillClass(s: ActionItemSeverity): string {
  switch (s) {
    case "critical":
      return "pillBad";
    case "high":
      return "pillWarn";
    case "medium":
      return "pillNeutral";
    case "low":
      return "pillNeutral";
  }
}

export function statusPillClass(s: ActionItemStatus): string {
  switch (s) {
    case "resolved":
      return "pillOk";
    case "dismissed":
      return "pillBad";
    case "in_progress":
      return "pillWarn";
    case "deferred":
    case "acknowledged":
    case "open":
      return "pillNeutral";
  }
}

export function sourceKindLabel(k: ActionItemSourceKind): string {
  switch (k) {
    case "manual":
      return "Manual";
    case "watch":
      return "Watch";
    case "pattern_review":
      return "Pattern review";
    case "user_provision":
      return "User provision";
    case "system":
      return "Atlas";
    case "collab_session":
      return "Raised in a meeting";
  }
}

export function roleLabel(r: RASCIRole): string {
  return r.charAt(0).toUpperCase() + r.slice(1);
}

export function roleBadge(r: RASCIRole): string {
  switch (r) {
    case "responsible":
      return "R";
    case "accountable":
      return "A";
    case "support":
      return "S";
    case "consulted":
      return "C";
    case "informed":
      return "I";
  }
}

export function formatDue(due: string | null): string {
  if (!due) return "—";
  const d = new Date(due);
  return d.toLocaleDateString();
}

export function formatAge(createdAt: string): string {
  const then = new Date(createdAt).getTime();
  const now = Date.now();
  const sec = Math.max(0, Math.floor((now - then) / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 48) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 60) return `${day}d`;
  const mo = Math.floor(day / 30);
  return `${mo}mo`;
}

/** Absolute timestamps only (standing rule) — re-exported from the house formatter. */
export { formatTimestamp } from "./formatTime.js";

// -----------------------------------------------------------------------
// Activity payload formatter. Takes the raw (event_type, payload) from
// atlas_action_item_activity and returns a human-readable sentence.
// **Never render the raw payload JSON in the UI** — that directive is in
// memory (feedback_never_show_json_to_users.md).
// -----------------------------------------------------------------------

const NOTIFICATION_TYPE_LABEL: Record<string, string> = {
  action_item_created: "creation",
  action_item_status_changed: "status change",
  action_item_refired: "refire",
  action_item_escalated: "escalation"
};

/** Absolute calendar date for a due-date activity payload (never relative). */
function formatActivityDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

export function formatActivity(
  eventType: string,
  payload: Record<string, unknown>,
  memberLabel: (id: string | null) => string
): string {
  switch (eventType) {
    case "created":
      return "Created this item";
    case "status_change": {
      const from = typeof payload.from === "string" ? payload.from : "?";
      const to = typeof payload.to === "string" ? payload.to : "?";
      // The closure-notes body (when present on a closing transition) is
      // rendered separately by the timeline as a comment-style block — see
      // `extractClosureNoteBody` below — so the headline here stays terse.
      return `Changed status from ${from} to ${to}`;
    }
    case "reassign": {
      const to = typeof payload.to === "string" ? payload.to : null;
      if (!to) return "Unassigned this item";
      return `Reassigned to ${memberLabel(to)}`;
    }
    case "priority_change": {
      const from = typeof payload.from === "string" ? payload.from : "?";
      const to = typeof payload.to === "string" ? payload.to : "?";
      return `Changed priority from ${from} to ${to}`;
    }
    case "severity_change": {
      const from = typeof payload.from === "string" ? payload.from : "?";
      const to = typeof payload.to === "string" ? payload.to : "?";
      return `Changed severity from ${from} to ${to}`;
    }
    case "due_at_change": {
      const from = typeof payload.from === "string" ? formatActivityDate(payload.from) : null;
      const to = typeof payload.to === "string" ? formatActivityDate(payload.to) : null;
      if (from && to) return `Changed due date from ${from} to ${to}`;
      if (to) return `Set due date to ${to}`;
      if (from) return `Cleared the due date (was ${from})`;
      return "Changed due date";
    }
    case "title_change": {
      const from = typeof payload.from === "string" ? payload.from : null;
      const to = typeof payload.to === "string" ? payload.to : null;
      if (from && to) return `Renamed from "${from}" to "${to}"`;
      return to ? `Renamed to "${to}"` : "Changed the title";
    }
    case "description_change": {
      const from = typeof payload.from === "string" && payload.from.trim() ? payload.from : null;
      const to = typeof payload.to === "string" && payload.to.trim() ? payload.to : null;
      if (!from && to) return "Added a description";
      if (from && !to) return "Cleared the description";
      return "Updated the description";
    }
    case "comment":
      return "Added a comment";
    case "closure_notes_edited":
      // The note body is rendered separately by the timeline (see
      // `extractClosureNoteBody`) as a comment-style block so line breaks
      // and full text are preserved. Headline stays terse.
      return "Updated closure notes";
    case "notified": {
      const type = typeof payload.type === "string" ? payload.type : "event";
      const count = typeof payload.recipient_count === "number" ? payload.recipient_count : null;
      const failed = payload.failed === true;
      const typeLabel = NOTIFICATION_TYPE_LABEL[type] ?? type.replace(/^action_item_/, "").replace(/_/g, " ");
      if (failed) return `Notification delivery for ${typeLabel} failed`;
      if (count === null) return `Notified recipients about ${typeLabel}`;
      return `Notified ${count} recipient${count === 1 ? "" : "s"} about ${typeLabel}`;
    }
    case "escalated": {
      const tier = typeof payload.tier_number === "number" ? payload.tier_number : null;
      return tier !== null ? `Escalated to tier ${tier}` : "Escalated";
    }
    case "refire":
      return "Fired again";
    default:
      return eventType.replace(/_/g, " ");
  }
}

/**
 * Return the closure-note text carried in an activity event payload, or
 * `null` if the event has no body to render. Used by the activity stream
 * to render the note as a comment-style block beneath the headline,
 * preserving whitespace and line breaks.
 *
 * Sources:
 *   * `status_change` events on closing transitions carry `closure_notes`
 *     in the payload (Phase 4 polish §3.3).
 *   * `closure_notes_edited` events (post-closure PATCH audit row, Phase 4
 *     polish 2026-04-26) carry the new value as `to`.
 *   * `description_change` events (migration 266) carry the new
 *     description as `to`; it renders the same way so the reader sees the
 *     text that replaced the old one.
 */
export function extractClosureNoteBody(
  eventType: string,
  payload: Record<string, unknown>
): string | null {
  if (eventType === "status_change" && typeof payload.closure_notes === "string") {
    const v = payload.closure_notes.trim();
    return v.length > 0 ? v : null;
  }
  if ((eventType === "closure_notes_edited" || eventType === "description_change") && typeof payload.to === "string") {
    const v = payload.to.trim();
    return v.length > 0 ? v : null;
  }
  return null;
}

// -----------------------------------------------------------------------
// Transition metadata (spec §16.1 legal edges + required codes).
// -----------------------------------------------------------------------

export type TransitionTarget = Exclude<ActionItemStatus, "open">;

/**
 * ADR-0154 §8.1 — one legal move, as the hub serves it on every action-item
 * payload. `requires` carries hub field names (the vocabulary its refusals
 * already use), so the console never has to translate a rule, only a label.
 */
export interface AllowedTransition {
  to: ActionItemStatus;
  requires: string[];
}

export interface TransitionMeta {
  target: TransitionTarget;
  label: string;
  variant: "primary" | "default" | "danger";
  requires: {
    deferral_reason?: boolean;
    deferred_until?: boolean;
    resolution?: boolean;
    root_cause?: boolean;
    dismissal?: boolean;
  };
}


// -----------------------------------------------------------------------
// Status dropdown options (Phase 4 polish §3.2).
//
// The detail page uses a single Status <select> in place of the old
// transition-button row. `statusOptionsFor(currentStatus)` returns the
// dropdown's option list, keeping the current status as a disabled
// selected entry and `acknowledged` as a disabled system-only entry where
// appropriate.
//
// `closure_notes` validation, code requirements (resolution / root_cause /
// dismissal / deferral_reason / deferred_until) are surfaced through the
// shared LEGAL_TRANSITIONS metadata above — this helper is purely about
// what shows up in the dropdown UI.
// -----------------------------------------------------------------------

export interface StatusOption {
  /** Status value submitted on transition. */
  value: ActionItemStatus;
  /** UI label for the dropdown option. */
  label: string;
  /** True when the option is rendered but not selectable (current state, terminal, system-only). */
  disabled: boolean;
}

const STATUS_LABELS: Record<ActionItemStatus, string> = {
  open: "Open",
  acknowledged: "Acknowledged",
  in_progress: "In progress",
  deferred: "Deferred",
  resolved: "Resolved",
  dismissed: "Dismissed"
};

/**
 * Build the Status dropdown option list for the detail page.
 *
 * The user's saved status is selectable so picking it reverts a pending
 * unsaved change (autosave skips when editForm matches the saved snapshot).
 * `acknowledged` is disabled when the saved status is `open` because that
 * promotion is system-driven (auto-ack on assignee view). Terminals
 * (resolved / dismissed) return only the current value — the parent
 * `<select>` is also disabled at the field level when `itemTerminal`.
 */
/**
 * ADR-0154 §6.3 C1 — build the Status dropdown from what the SERVER says is
 * legal for this item.
 *
 * This replaces two console-side transition tables that disagreed with the hub
 * and with each other. From `open`, the old dropdown offered `in_progress`,
 * `resolved` and `dismissed` as enabled — all three refused by the hub — and
 * from `deferred` it offered both terminals, also refused. A user could pick a
 * status, fill in the closure fields, save, and only then be told no.
 *
 * The console now renders the served `allowed_transitions` and owns nothing but
 * labels. An unknown status coming back from a newer hub is humanized rather
 * than hidden, so this list can never again be the thing that suppresses a
 * legal action.
 */
export function statusOptionsFromServed(
  currentStatus: ActionItemStatus,
  allowed: readonly AllowedTransition[]
): StatusOption[] {
  const current: StatusOption = {
    value: currentStatus,
    // Selectable so picking it reverts a pending unsaved change; disabled when
    // there is nowhere to go, which is how a terminal presents itself.
    label: statusLabel(currentStatus),
    disabled: allowed.length === 0,
  };
  return [current, ...allowed.map((t) => ({ value: t.to, label: statusLabel(t.to), disabled: false }))];
}

/** Label for a status, humanizing anything a newer hub introduces. */
export function statusLabel(status: string): string {
  return STATUS_LABELS[status as ActionItemStatus] ?? humanizeStatus(status);
}

function humanizeStatus(status: string): string {
  return status.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

/**
 * Set of fields the hub validator requires for a given target status.
 * Mirrors `validateTransition` in the hub repo. Used by the detail page
 * to drive inline validation before the user clicks Save.
 */
/**
 * ADR-0154 §6.3 C1 — which fields the hub will require for a transition,
 * read from the SERVED contract rather than mirrored from its validator.
 *
 * The previous version of this function was a hand-written mirror of
 * `validateTransition`, with a comment saying so. The hub now derives its
 * requirements BY ASKING the validator (see `allowedTransitionsFor`), and the
 * console reads that answer. What remains here is a naming adapter — the hub
 * speaks in column names, the form fields are grouped slightly differently —
 * and `KNOWN_REQUIREMENT_FIELDS` keeps it total: a requirement the hub sends
 * that this adapter does not recognise is surfaced, never dropped, because a
 * silently-ignored requirement is a form that saves and then fails.
 */
export interface RequiredFields {
  deferral_reason: boolean;
  deferred_until: boolean;
  resolution: boolean;
  root_cause: boolean;
  dismissal: boolean;
  closure_notes: boolean;
  /** Served requirements this console build does not know how to render. */
  unknown: string[];
}

/** Hub requirement field → the form group that collects it. */
const REQUIREMENT_FIELD_TO_FORM_GROUP: Record<string, keyof Omit<RequiredFields, "unknown">> = {
  deferral_reason_code_id: "deferral_reason",
  deferred_until: "deferred_until",
  resolution_code_id: "resolution",
  root_cause_code_id: "root_cause",
  dismissal_code_id: "dismissal",
  closure_notes: "closure_notes",
};

export const KNOWN_REQUIREMENT_FIELDS: readonly string[] = Object.keys(REQUIREMENT_FIELD_TO_FORM_GROUP);

export function requiredFieldsFromServed(
  allowed: readonly AllowedTransition[],
  target: ActionItemStatus
): RequiredFields {
  const out: RequiredFields = {
    deferral_reason: false,
    deferred_until: false,
    resolution: false,
    root_cause: false,
    dismissal: false,
    closure_notes: false,
    unknown: [],
  };
  const transition = allowed.find((t) => t.to === target);
  for (const field of transition?.requires ?? []) {
    const group = REQUIREMENT_FIELD_TO_FORM_GROUP[field];
    if (group) out[group] = true;
    else out.unknown.push(field);
  }
  return out;
}

// -----------------------------------------------------------------------
// Bulk dismissal (POST /tenant/action-items/bulk-dismiss).
//
// Partial success is the normal case — a caller can be authorized on some
// of a selection and not others, and items close underneath a list that's
// been on screen a while. The hub answers per id; nothing here collapses
// that to a single pass/fail.
// -----------------------------------------------------------------------

export type BulkDismissOutcome =
  | "dismissed"
  | "skipped_terminal"
  | "forbidden"
  | "not_found"
  | "unreachable"
  | "failed";

export interface BulkDismissResult {
  id: string;
  /** Item title, or null when the id resolved to nothing. Never render the id. */
  title: string | null;
  outcome: BulkDismissOutcome;
  message?: string;
  /** True when the item passed through `acknowledged` on its way out. */
  acknowledged_first?: boolean;
}

export interface BulkDismissResponse {
  results: BulkDismissResult[];
  summary: Record<BulkDismissOutcome, number>;
}

/**
 * Plain-language label for an outcome, in the plural the summary line needs.
 * Total over the union so a new hub outcome can't render as a bare token.
 */
export function bulkDismissOutcomeLabel(outcome: BulkDismissOutcome): string {
  switch (outcome) {
    case "dismissed":
      return "dismissed";
    case "skipped_terminal":
      return "already closed";
    case "forbidden":
      return "not permitted";
    case "not_found":
      return "no longer exist";
    case "unreachable":
      return "cannot be dismissed from their current status";
    case "failed":
      return "failed";
  }
}

/** Per-row reason shown beside an item that wasn't dismissed. */
export function bulkDismissOutcomeReason(result: BulkDismissResult): string {
  if (result.message) return result.message;
  switch (result.outcome) {
    case "dismissed":
      return "Dismissed";
    case "skipped_terminal":
      return "Already closed";
    case "forbidden":
      return "You don't have permission to close this item";
    case "not_found":
      return "No longer exists";
    case "unreachable":
      return "Cannot be dismissed from its current status";
    case "failed":
      return "Failed";
  }
}

/**
 * One-line summary of a bulk dismissal — "9 dismissed, 2 not permitted,
 * 1 already closed". Built from whatever outcomes actually occurred, in the
 * order the summary object lists them, so a zero bucket never shows up.
 */
export function summarizeBulkDismiss(summary: Partial<Record<BulkDismissOutcome, number>>): string {
  const parts = (Object.entries(summary) as Array<[BulkDismissOutcome, number]>)
    .filter(([, n]) => typeof n === "number" && n > 0)
    .map(([outcome, n]) => `${n} ${bulkDismissOutcomeLabel(outcome)}`);
  return parts.length > 0 ? parts.join(", ") : "Nothing to dismiss";
}

// -----------------------------------------------------------------------
// Hard delete (POST /tenant/action-items/bulk-delete).
//
// Deleting is for items that should never have existed — a misconfigured
// watch minted them. Dismissing is for items that were considered and need
// no action. The two are not substitutes, so both live on the list.
//
// Only watch-sourced items can be deleted, and only by a tenant admin. Both
// rules are enforced in the hub; the console mirrors them so it never
// offers a button that is going to be refused.
// -----------------------------------------------------------------------

/** Source kinds the hub will hard-delete. Mirrors `DELETABLE_SOURCE_KINDS`. */
export const DELETABLE_SOURCE_KINDS: readonly ActionItemSourceKind[] = ["watch"];

export function isDeletableSourceKind(kind: ActionItemSourceKind): boolean {
  return DELETABLE_SOURCE_KINDS.includes(kind);
}

export type DeleteRefusalReason = "not_found" | "not_deletable_source";

export interface BulkDeleteResponse {
  deleted: Array<{ id: string; title: string }>;
  /**
   * How many watch row-state entries the delete re-armed (2026-08-17).
   * Deleting a watch-sourced item also clears the watch's memory of having
   * fired for that identity, so a still-matching row raises a fresh item on
   * the next evaluation. Optional: an older hub omits it.
   */
  watch_tracking_cleared?: number;
  refused: Array<{ id: string; title: string | null; reason: DeleteRefusalReason }>;
}

/** Why an item in a delete batch was left alone — a sentence, not a token. */
export function deleteRefusalReasonLabel(reason: DeleteRefusalReason): string {
  switch (reason) {
    case "not_found":
      return "No longer exists";
    case "not_deletable_source":
      return "Not watch-generated — dismiss it instead";
  }
}

/** Map from a `missing_fields` entry returned by the hub to a user-facing label. */
export const MISSING_FIELD_LABEL: Record<string, string> = {
  deferral_reason_code_id: "Deferral reason",
  deferred_until: "Deferred until",
  resolution_code_id: "Resolution code",
  root_cause_code_id: "Root cause code",
  dismissal_code_id: "Dismissal code",
  closure_notes: "Closure notes"
};
